import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import {
  DATE_RANGE_PRESETS,
  getPresetRange,
  habitConsistency,
  overallGoalProgress,
  useGetCompletions,
  useGetGoalLinkedTasks,
  useGetInsights,
  useGetUserHabits,
  useGoalRefinements,
  type DateRangePreset,
  type LocalDateRange,
} from '@zendia/frontend-shared'
import { ActionBar } from '../../../components/ActionBar'
import { Alert, AlertVariants } from '../../../components/Alert'
import {
  DateRangeDropdown,
  type DateRangeSelection,
} from '../../../components/Insights/DateRangeDropdown'
import { LifeBalanceCard } from '../../../components/Insights/LifeBalanceCard'
import { ProductivityExplanation } from '../../../components/Insights/ProductivityExplanation'
import { ProductivityTrendCard } from '../../../components/Insights/ProductivityTrendCard'
import { ICONS, days } from '../../../components/Insights/insightIcons'
import { StatsCard } from '../../../components/Insights/StatsCard'
import { TaskHabitCompletionCard } from '../../../components/Insights/TaskHabitCompletionCard'
import { axiosAuthenticatedRequest } from '../../../utilities/axiosClient'
import { useToday } from '../../../hooks/useToday'

const DEFAULT_SELECTION: DateRangePreset = 'monthToDate'
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

// The range lives in the URL so a refresh or Back keeps it. A preset is stored by name, not by
// its dates, so returning to "Today" tomorrow shows tomorrow.
const insightsSearchSchema = z.object({
  range: z
    .enum([...DATE_RANGE_PRESETS, 'custom'])
    .optional()
    .catch(undefined),
  from: daySchema.optional().catch(undefined),
  to: daySchema.optional().catch(undefined),
})

export const Route = createFileRoute('/_authenticated/insights/')({
  component: InsightsPage,
  validateSearch: insightsSearchSchema,
})

const goalsCount = (count: number) => `${count} ${count === 1 ? 'goal' : 'goals'}`

function InsightsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Re-renders at local midnight, so a preset like "Today" moves on with the day
  const today = useToday()

  const selection = search.range ?? DEFAULT_SELECTION
  const customRange = useMemo<LocalDateRange>(() => {
    const fallback = getPresetRange(DEFAULT_SELECTION)
    const startDate = search.from ?? fallback.startDate
    const endDate = search.to ?? fallback.endDate
    return startDate <= endDate ? { startDate, endDate } : fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` re-derives the fallback
  }, [search.from, search.to, today])
  const dateRange = useMemo<LocalDateRange>(
    () => (selection === 'custom' ? customRange : getPresetRange(selection)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` re-derives the preset
    [selection, customRange, today],
  )

  const {
    data: response,
    isLoading,
    isError,
  } = useGetInsights(dateRange, axiosAuthenticatedRequest)
  const insights = response?.data

  // Goal Progress and Habit Consistency are all-time figures, so none of these follow the range.
  // Both are worked out here, where the goal page and the dashboard work them out too.
  const goals = useGoalRefinements(axiosAuthenticatedRequest)
  const goalTasks = useGetGoalLinkedTasks(axiosAuthenticatedRequest)
  const habits = useGetUserHabits(axiosAuthenticatedRequest)
  const completions = useGetCompletions(axiosAuthenticatedRequest)

  const goalsList = goals.data?.data
  const habitsList = habits.data?.data?.habits
  const completionsList = completions.data?.data?.completions
  const goalsLoaded = !!goalsList && !!goalTasks.data
  const habitsLoaded = !!habitsList && !!completionsList

  const goalProgress = useMemo(
    () => (goalsList && goalTasks.data ? overallGoalProgress(goalsList, goalTasks.data) : null),
    [goalsList, goalTasks.data],
  )
  const consistency = useMemo(
    () =>
      habitsList && completionsList
        ? habitConsistency({ habits: habitsList, completions: completionsList, today })
        : null,
    [habitsList, completionsList, today],
  )
  const isLoadingKeyStatistics =
    isLoading || goals.isLoading || goalTasks.isLoading || habits.isLoading || completions.isLoading

  const changeSearch = (next: z.infer<typeof insightsSearchSchema>) =>
    navigate({ search: next, replace: true })

  const handleSelectionChange = (next: DateRangeSelection) =>
    changeSearch(
      // Custom opens on the range that was showing, so it starts from something sensible
      next === 'custom'
        ? { range: next, from: dateRange.startDate, to: dateRange.endDate }
        : { range: next },
    )

  const completionRate =
    insights && insights.tasksTotal > 0
      ? Math.round((insights.tasksCompleted / insights.tasksTotal) * 100)
      : null

  return (
    <>
      <ActionBar title="Insights" />
      <div className="flex flex-col gap-6 p-4 max-w-6xl w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <p className="text-[var(--color-body-text-secondary)]">
            Your journey toward balanced, meaningful living
          </p>
          <DateRangeDropdown
            selection={selection}
            customRange={customRange}
            onSelectionChange={handleSelectionChange}
            onCustomRangeChange={(range) =>
              changeSearch({ range: 'custom', from: range.startDate, to: range.endDate })
            }
          />
        </div>

        <section>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Life Balance</h2>
          <LifeBalanceCard dateRange={dateRange} />
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-1 text-[var(--color-heading)]">
            Productivity &amp; Intentional Living
          </h2>
          <div className="mb-4">
            <ProductivityExplanation />
          </div>
          <ProductivityTrendCard dateRange={dateRange} />
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">
            Task &amp; Habit Completion
          </h2>
          <TaskHabitCompletionCard dateRange={dateRange} />
        </section>

        {isError && (
          <Alert type={AlertVariants.error}>
            We couldn't load your insights. Please try again.
          </Alert>
        )}

        <section aria-busy={isLoadingKeyStatistics}>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Key Statistics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatsCard
              title="Total Days"
              value={insights ? days(insights.totalActiveDays) : '-'}
              subtitle="Using Zendia, all time"
              icon={ICONS.calendar}
            />
            <StatsCard
              title="Days Active"
              value={insights ? days(insights.activeDaysInRange) : '-'}
              subtitle="In this range"
              icon={ICONS.sun}
            />
            <StatsCard
              title="Journaling Days"
              value={insights ? days(insights.journalingDays) : '-'}
              icon={ICONS.pencil}
            />
            <StatsCard
              title="Journal Entries"
              value={insights ? insights.journalEntries : '-'}
              icon={ICONS.book}
            />
            <StatsCard
              title="Tasks Completed"
              value={insights ? `${insights.tasksCompleted} / ${insights.tasksTotal}` : '-'}
              subtitle={completionRate === null ? undefined : `${completionRate}% completion rate`}
              icon={ICONS.check}
            />
            <StatsCard
              title="Goal Progress"
              value={goalProgress ? `${Math.round(goalProgress.percent)}%` : '-'}
              subtitle={
                !goalsLoaded
                  ? undefined
                  : goalProgress
                    ? `Across ${goalsCount(goalProgress.goals)}`
                    : 'No goals with a plan yet'
              }
              icon={ICONS.chart}
            />
            <StatsCard
              title="Habit Consistency"
              value={consistency ? `${Math.round(consistency.percent)}%` : '-'}
              subtitle={
                !habitsLoaded
                  ? undefined
                  : consistency
                    ? 'All habits, all time'
                    : 'No habits due yet'
              }
              icon={ICONS.repeat}
            />
          </div>
        </section>

        <section aria-busy={isLoading}>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Streaks</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatsCard
              title="Current Streak"
              value={insights ? days(insights.currentStreak) : '-'}
              subtitle="Keep it going!"
              icon={ICONS.flame}
            />
            <StatsCard
              title="Longest Streak"
              value={insights ? days(insights.longestStreak) : '-'}
              subtitle="Personal best"
              icon={ICONS.sparkle}
            />
          </div>
        </section>
      </div>
    </>
  )
}
